import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Trash2, Save, X, Edit3, Users, Loader2, CheckCircle2, XCircle,
  Upload, FileText, Pause, Play, AlertTriangle, RefreshCw, Filter, Download,
  ChevronDown, BadgeCheck, ShieldQuestion, ShieldX, ShieldAlert,
} from 'lucide-react';

const API = '/api';

const CATEGORY_OPTIONS = [
  'solar', 'batteries', 'ev_charger', 'heat_pumps', 'boilers',
  'ventilation', 'glazing', 'insulation',
];

const PROVINCES = [
  'Drenthe', 'Flevoland', 'Friesland', 'Gelderland', 'Groningen',
  'Limburg', 'Noord-Brabant', 'Noord-Holland', 'Overijssel',
  'Utrecht', 'Zeeland', 'Zuid-Holland',
];

const STATUS_META = {
  imported:         { label: 'Imported',        cls: 'bg-slate-100 text-slate-700',   Icon: ShieldQuestion },
  ready_for_review: { label: 'Ready for review', cls: 'bg-blue-100 text-blue-700',     Icon: ShieldAlert },
  active:           { label: 'Active',          cls: 'bg-green-100 text-green-700',   Icon: BadgeCheck },
  needs_check:      { label: 'Needs check',     cls: 'bg-amber-100 text-amber-700',   Icon: AlertTriangle },
  rejected:         { label: 'Rejected',        cls: 'bg-red-100 text-red-700',       Icon: ShieldX },
  paused:           { label: 'Paused',          cls: 'bg-slate-100 text-slate-700',   Icon: Pause },
};

const SOURCE_OPTIONS = ['', 'google', 'trustpilot', 'klantenvertellen', 'kvk', 'other'];
const AFFILIATE_OPTIONS = ['unknown', 'yes', 'no', 'contacted', 'interested', 'approved'];

const AFFILIATE_META = {
  unknown:    { label: 'Unknown',    cls: 'bg-slate-100 text-slate-600' },
  yes:        { label: 'Yes',        cls: 'bg-emerald-100 text-emerald-700' },
  no:         { label: 'No',         cls: 'bg-red-100 text-red-700' },
  contacted:  { label: 'Contacted',  cls: 'bg-blue-100 text-blue-700' },
  interested: { label: 'Interested', cls: 'bg-blue-100 text-blue-700' },
  approved:   { label: 'Approved',   cls: 'bg-green-100 text-green-700' },
};

const emptyForm = {
  company: '', contact_name: '', email: '', phone: '', website: '',
  categories: [],
  postcode_ranges: '',  // user types "1000-1099|1100-1199"
  service_cities: '',
  province: '',         // UI exposes a multi-select for provinces; we'll keep a string list joined with |
  provinces: [],        // array of canonical province names
  nationwide: false,
  google_rating: '', review_count: '', review_source: '',
  affiliate_open: 'unknown',
  response_time_tested: false,
  response_time_hours: '',
  quality_notes: '',
  manual_risk_flags: '',  // pipe-separated string in form
  priority: 5,
  notes: '',
};

function StatusPill({ status }) {
  const meta = STATUS_META[status] || STATUS_META.imported;
  const Icon = meta.Icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full ${meta.cls}`}>
      <Icon className="w-3 h-3" /> {meta.label}
    </span>
  );
}

function ScoreBadge({ score }) {
  const s = Number(score) || 0;
  const cls = s >= 70 ? 'bg-emerald-100 text-emerald-700' : s >= 40 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700';
  return <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${cls}`}>Score {s}</span>;
}

function rangesToString(ranges) {
  if (!ranges || !ranges.length) return '';
  return ranges.map(r => `${r.from}-${r.to}`).join('|');
}
function stringToRanges(raw) {
  if (!raw || !raw.trim()) return [];
  const parts = raw.split('|').map(s => s.trim()).filter(Boolean);
  const out = [];
  for (const part of parts) {
    const m = part.match(/^(\d{4})\s*-\s*(\d{4})$/);
    if (!m) throw new Error(`Invalid range "${part}" (expected NNNN-NNNN)`);
    if (parseInt(m[1]) > parseInt(m[2])) throw new Error(`"${part}": from > to`);
    out.push({ from: m[1], to: m[2] });
  }
  return out;
}

// ---- Import panel -----------------------------------------------------

function ImportPanel({ token, onDone }) {
  const [csvText, setCsvText] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const opts = { headers: { Cookie: `access_token=${token}` }, withCredentials: true };

  const submitText = async () => {
    if (!csvText.trim()) {
      toast.error('Paste CSV content first');
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/providers/import`, { csv_text: csvText }, opts);
      setResult(data);
      toast.success(`Imported ${data.created} provider(s)`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import failed');
    }
    setBusy(false);
  };

  const submitFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/admin/providers/import-file`, fd, {
        ...opts,
        headers: { ...opts.headers, 'Content-Type': 'multipart/form-data' },
      });
      setResult(data);
      toast.success(`Imported ${data.created} provider(s)`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import failed');
    }
    setBusy(false);
  };

  return (
    <div className="bg-white border border-blue-200 rounded-xl p-4 space-y-3" data-testid="import-panel">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[250px]">
          <h4 className="text-sm font-semibold flex items-center gap-2">
            <Upload className="w-4 h-4 text-blue-600" /> Mass import providers
          </h4>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            Imported providers always land in <strong>ready_for_review</strong> or lower — never auto-active. Use the template for the exact column format.
          </p>
        </div>
        <a
          href={`${API}/admin/providers/import-template.csv`}
          className="px-3 py-1.5 text-xs bg-muted text-foreground rounded hover:bg-muted/80 flex items-center gap-1"
          download
          data-testid="download-template-btn"
        >
          <Download className="w-3 h-3" /> Template CSV
        </a>
      </div>

      <textarea
        value={csvText}
        onChange={(e) => setCsvText(e.target.value)}
        rows={6}
        placeholder="Paste CSV content here (or use file upload below)"
        className="w-full text-xs border border-border rounded p-2 font-mono"
        data-testid="csv-textarea"
      />
      <div className="flex flex-wrap gap-2 items-center">
        <button
          onClick={submitText}
          disabled={busy}
          className="px-3 py-1.5 text-xs bg-primary text-white rounded hover:opacity-90 disabled:opacity-50 flex items-center gap-1"
          data-testid="import-paste-btn"
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />}
          Import paste
        </button>

        <label className="px-3 py-1.5 text-xs bg-white border border-blue-300 text-blue-700 rounded hover:bg-blue-50 cursor-pointer flex items-center gap-1" data-testid="import-file-btn">
          <Upload className="w-3 h-3" /> Upload CSV file
          <input
            type="file"
            accept=".csv,text/csv,text/plain"
            className="hidden"
            onChange={(e) => submitFile(e.target.files?.[0])}
          />
        </label>

        {result && (
          <span className="text-[11px] text-muted-foreground ml-2">
            Last: {result.created} created, {result.skipped} skipped, {result.errors?.length || 0} errors
          </span>
        )}
      </div>

      {result && (
        <div className="bg-muted/30 rounded p-2 text-[11px] space-y-1" data-testid="import-result">
          <div className="flex flex-wrap gap-3">
            <span><strong>{result.created}</strong> created</span>
            <span><strong>{result.skipped}</strong> skipped</span>
            <span><strong>{result.errors?.length || 0}</strong> errors</span>
            {result.status_breakdown && Object.entries(result.status_breakdown).map(([s, n]) => (
              <span key={s} className="text-muted-foreground">{s}: <strong>{n}</strong></span>
            ))}
          </div>
          {(result.errors || []).length > 0 && (
            <details className="text-[10px]">
              <summary className="cursor-pointer text-muted-foreground">Show errors ({result.errors.length})</summary>
              <ul className="mt-1 space-y-0.5 max-h-40 overflow-auto">
                {result.errors.slice(0, 50).map((e, i) => (
                  <li key={i} className="text-red-700">row {e.row_number}: {e.reason}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

// ---- Edit form -------------------------------------------------------

function EditForm({ initial, onCancel, onSaved, token }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const opts = { headers: { Cookie: `access_token=${token}` }, withCredentials: true };

  const toggleCategory = (cat) => {
    setForm(f => ({
      ...f,
      categories: f.categories.includes(cat) ? f.categories.filter(c => c !== cat) : [...f.categories, cat],
    }));
  };

  const save = async () => {
    if (!form.company || !form.email || form.categories.length === 0) {
      toast.error('Company, email and at least one category required');
      return;
    }
    let ranges;
    try {
      ranges = stringToRanges(form.postcode_ranges);
    } catch (e) {
      toast.error(e.message);
      return;
    }

    setSaving(true);
    const payload = {
      company: form.company.trim(),
      contact_name: form.contact_name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      website: form.website.trim(),
      categories: form.categories,
      postcode_ranges: ranges,
      service_cities: form.service_cities.split(',').map(s => s.trim()).filter(Boolean),
      provinces: form.provinces || [],
      province: (form.provinces && form.provinces[0]) || form.province || '',
      nationwide: !!form.nationwide,
      google_rating: parseFloat(form.google_rating) || 0,
      review_count: parseInt(form.review_count) || 0,
      review_source: form.review_source || '',
      affiliate_open: form.affiliate_open || 'unknown',
      response_time_tested: !!form.response_time_tested,
      response_time_hours: parseInt(form.response_time_hours) || 0,
      quality_notes: form.quality_notes || '',
      manual_risk_flags: (form.manual_risk_flags || '').split('|').map(s => s.trim()).filter(Boolean),
      priority: parseInt(form.priority) || 5,
      notes: form.notes,
    };
    try {
      if (form._id) {
        await axios.put(`${API}/admin/providers/${form._id}`, payload, opts);
        toast.success('Provider updated');
      } else {
        await axios.post(`${API}/admin/providers`, payload, opts);
        toast.success('Provider added (ready_for_review)');
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  return (
    <div className="bg-white border border-primary/40 rounded-xl p-4 space-y-3" data-testid="provider-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="text-xs">
          <span className="block text-muted-foreground mb-1">Company *</span>
          <input value={form.company} onChange={e => setForm({ ...form, company: e.target.value })}
            className="w-full border border-border rounded px-2 py-1.5 text-sm" data-testid="provider-company-input" />
        </label>
        <label className="text-xs">
          <span className="block text-muted-foreground mb-1">Contact name</span>
          <input value={form.contact_name} onChange={e => setForm({ ...form, contact_name: e.target.value })}
            className="w-full border border-border rounded px-2 py-1.5 text-sm" data-testid="provider-name-input" />
        </label>
        <label className="text-xs">
          <span className="block text-muted-foreground mb-1">Email *</span>
          <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
            className="w-full border border-border rounded px-2 py-1.5 text-sm" data-testid="provider-email-input" />
        </label>
        <label className="text-xs">
          <span className="block text-muted-foreground mb-1">Phone</span>
          <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
            className="w-full border border-border rounded px-2 py-1.5 text-sm" data-testid="provider-phone-input" />
        </label>
        <label className="text-xs col-span-1 md:col-span-2">
          <span className="block text-muted-foreground mb-1">Website</span>
          <input value={form.website} onChange={e => setForm({ ...form, website: e.target.value })}
            placeholder="https://example.nl"
            className="w-full border border-border rounded px-2 py-1.5 text-sm" data-testid="provider-website-input" />
        </label>
        <label className="text-xs">
          <span className="block text-muted-foreground mb-1">Postcode ranges (e.g. 1000-1099|1100-1199)</span>
          <input value={form.postcode_ranges} onChange={e => setForm({ ...form, postcode_ranges: e.target.value })}
            className="w-full border border-border rounded px-2 py-1.5 text-sm" data-testid="provider-ranges-input" />
        </label>
        <label className="text-xs">
          <span className="block text-muted-foreground mb-1">Service cities (comma-separated)</span>
          <input value={form.service_cities} onChange={e => setForm({ ...form, service_cities: e.target.value })}
            className="w-full border border-border rounded px-2 py-1.5 text-sm" data-testid="provider-cities-input" />
        </label>
        <label className="text-xs">
          <span className="block text-muted-foreground mb-1">Provinces (multi-select)</span>
          <select
            multiple
            value={form.provinces || []}
            onChange={e => setForm({ ...form, provinces: Array.from(e.target.selectedOptions).map(o => o.value) })}
            className="w-full border border-border rounded px-2 py-1.5 text-sm bg-white h-24"
            data-testid="provider-province-input">
            {PROVINCES.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <span className="text-[10px] text-muted-foreground">Hold Ctrl/Cmd to select multiple. Pipe-separated codes (NH|ZH|UT) supported in CSV.</span>
        </label>
        <label className="text-xs flex items-center gap-2 pt-5">
          <input type="checkbox" checked={form.nationwide} onChange={e => setForm({ ...form, nationwide: e.target.checked })}
            data-testid="provider-nationwide-input" />
          <span>Nationwide (overrides ranges/cities for matching)</span>
        </label>
        <label className="text-xs">
          <span className="block text-muted-foreground mb-1">Google rating (0–5)</span>
          <input type="number" step="0.1" min="0" max="5" value={form.google_rating}
            onChange={e => setForm({ ...form, google_rating: e.target.value })}
            className="w-full border border-border rounded px-2 py-1.5 text-sm" data-testid="provider-rating-input" />
        </label>
        <label className="text-xs">
          <span className="block text-muted-foreground mb-1">Review count</span>
          <input type="number" min="0" value={form.review_count}
            onChange={e => setForm({ ...form, review_count: e.target.value })}
            className="w-full border border-border rounded px-2 py-1.5 text-sm" data-testid="provider-reviews-input" />
        </label>
        <label className="text-xs">
          <span className="block text-muted-foreground mb-1">Review source</span>
          <select value={form.review_source} onChange={e => setForm({ ...form, review_source: e.target.value })}
            className="w-full border border-border rounded px-2 py-1.5 text-sm bg-white">
            {SOURCE_OPTIONS.map(s => <option key={s} value={s}>{s || '— none —'}</option>)}
          </select>
        </label>
        <label className="text-xs">
          <span className="block text-muted-foreground mb-1">Priority (1 highest, 5 default)</span>
          <input type="number" min="1" max="9" value={form.priority}
            onChange={e => setForm({ ...form, priority: e.target.value })}
            className="w-full border border-border rounded px-2 py-1.5 text-sm" data-testid="provider-priority-input" />
        </label>

        {/* National-rollout extras */}
        <label className="text-xs">
          <span className="block text-muted-foreground mb-1">Affiliate openness</span>
          <select value={form.affiliate_open}
            onChange={e => setForm({ ...form, affiliate_open: e.target.value })}
            className="w-full border border-border rounded px-2 py-1.5 text-sm bg-white"
            data-testid="provider-affiliate-input">
            {AFFILIATE_OPTIONS.map(s => <option key={s} value={s}>{AFFILIATE_META[s].label}</option>)}
          </select>
        </label>
        <label className="text-xs flex items-center gap-2 pt-5">
          <input type="checkbox" checked={form.response_time_tested}
            onChange={e => setForm({ ...form, response_time_tested: e.target.checked })}
            data-testid="provider-resp-tested-input" />
          <span>Response time tested</span>
        </label>
        <label className="text-xs">
          <span className="block text-muted-foreground mb-1">Response time (hours)</span>
          <input type="number" min="0" value={form.response_time_hours}
            onChange={e => setForm({ ...form, response_time_hours: e.target.value })}
            disabled={!form.response_time_tested}
            className="w-full border border-border rounded px-2 py-1.5 text-sm disabled:bg-muted/40"
            data-testid="provider-resp-hours-input" />
        </label>
        <label className="text-xs col-span-1 md:col-span-2">
          <span className="block text-muted-foreground mb-1">Manual risk flags (pipe-separated, e.g. complaint_history|kvk_unknown)</span>
          <input value={form.manual_risk_flags}
            onChange={e => setForm({ ...form, manual_risk_flags: e.target.value })}
            className="w-full border border-border rounded px-2 py-1.5 text-sm"
            data-testid="provider-manual-flags-input" />
        </label>
      </div>

      <div>
        <p className="text-xs text-muted-foreground mb-1.5">Categories *</p>
        <div className="flex flex-wrap gap-1.5">
          {CATEGORY_OPTIONS.map(cat => (
            <button key={cat} type="button" onClick={() => toggleCategory(cat)}
              className={`text-[11px] px-2.5 py-1 rounded-full border ${form.categories.includes(cat) ? 'bg-primary text-white border-primary' : 'bg-white text-muted-foreground border-border hover:border-primary'}`}
              data-testid={`provider-cat-${cat}`}>
              {cat.replace(/_/g, ' ')}
            </button>
          ))}
        </div>
      </div>

      <label className="text-xs block">
        <span className="block text-muted-foreground mb-1">Quality notes (internal QA)</span>
        <textarea value={form.quality_notes}
          onChange={e => setForm({ ...form, quality_notes: e.target.value })}
          rows={2}
          className="w-full border border-border rounded px-2 py-1.5 text-sm"
          data-testid="provider-quality-notes-input" />
      </label>

      <label className="text-xs block">
        <span className="block text-muted-foreground mb-1">Internal notes</span>
        <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2}
          className="w-full border border-border rounded px-2 py-1.5 text-sm" />
      </label>

      <div className="flex gap-2">
        <button onClick={save} disabled={saving}
          className="px-3 py-2 text-xs bg-primary text-white rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center gap-1.5"
          data-testid="save-provider-btn">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          Save
        </button>
        <button onClick={onCancel}
          className="px-3 py-2 text-xs bg-muted text-foreground rounded-lg hover:bg-muted/80 flex items-center gap-1.5">
          <X className="w-3.5 h-3.5" /> Cancel
        </button>
      </div>
    </div>
  );
}

// ---- Main panel -----------------------------------------------------

export default function ProviderManagerPanel({ token }) {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [filters, setFilters] = useState({
    status: '', category: '', province: '', postcode: '',
    affiliate_open: '',
    min_score: '', min_rating: '', min_reviews: '',
    search: '', sort: 'score_desc',
  });
  const [selected, setSelected] = useState(new Set());

  const opts = useMemo(() => ({ headers: { Cookie: `access_token=${token}` }, withCredentials: true }), [token]);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v !== '' && v !== null && v !== undefined) params.set(k, v); });
      const { data } = await axios.get(`${API}/admin/providers?${params.toString()}`, opts);
      setProviders(data.providers || []);
    } catch {
      toast.error('Failed to load providers');
    }
    setLoading(false);
  };

  useEffect(() => { if (token) load(); /* eslint-disable-next-line */ }, [token, filters]);

  const startNew = () => setEditing({ ...emptyForm });
  const startEdit = (p) => setEditing({
    _id: p.provider_id,
    company: p.company || '',
    contact_name: p.contact_name || p.name || '',
    email: p.email || '',
    phone: p.phone || '',
    website: p.website || '',
    categories: p.categories || [],
    postcode_ranges: rangesToString(p.postcode_ranges),
    service_cities: (p.service_cities || []).join(', '),
    province: p.province || '',
    provinces: p.provinces || (p.province ? [p.province] : []),
    nationwide: !!p.nationwide,
    google_rating: p.google_rating ?? '',
    review_count: p.review_count ?? '',
    review_source: p.review_source || '',
    affiliate_open: p.affiliate_open || 'unknown',
    response_time_tested: !!p.response_time_tested,
    response_time_hours: p.response_time_hours ?? '',
    quality_notes: p.quality_notes || '',
    manual_risk_flags: (p.manual_risk_flags || []).join('|'),
    priority: p.priority ?? 5,
    notes: p.notes || '',
  });

  const setStatus = async (provider_id, status, reason) => {
    try {
      await axios.patch(`${API}/admin/providers/${provider_id}/status`, { status, reason: reason || '' }, opts);
      toast.success(`Status → ${STATUS_META[status]?.label || status}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Status update failed');
    }
  };

  const reject = async (provider_id) => {
    const reason = window.prompt('Reason for rejection?');
    if (!reason || !reason.trim()) return;
    return setStatus(provider_id, 'rejected', reason);
  };

  const toggleSelected = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());

  const bulkSetStatus = async (status) => {
    const ids = [...selected];
    if (!ids.length) return toast.error('Select providers first');
    if (ids.length > 50) return toast.error('Max 50 per bulk action');
    let reason = '';
    if (status === 'rejected') {
      reason = window.prompt('Reason for bulk rejection?') || '';
      if (!reason.trim()) return;
    }
    if (status === 'active' && !window.confirm(`Approve ${ids.length} provider(s) and make them eligible for matching?`)) return;
    try {
      const { data } = await axios.post(`${API}/admin/providers/bulk-status`, { provider_ids: ids, status, reason }, opts);
      toast.success(`Updated ${data.updated} provider(s)`);
      clearSelection();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk action failed');
    }
  };

  const updateFilter = (k, v) => setFilters(f => ({ ...f, [k]: v }));

  const counts = useMemo(() => {
    const c = {};
    for (const p of providers) c[p.status || 'imported'] = (c[p.status || 'imported'] || 0) + 1;
    return c;
  }, [providers]);

  return (
    <div className="space-y-4" data-testid="providers-tab">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <h2 className="text-lg font-medium flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" /> Providers
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Mass onboarding. Imported providers always start in <strong>ready_for_review</strong> — only manual approval activates them.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowImport(s => !s)}
            className="px-3 py-2 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-1.5"
            data-testid="toggle-import-btn">
            <Upload className="w-3.5 h-3.5" /> {showImport ? 'Hide import' : 'Mass import'}
          </button>
          {!editing && (
            <button onClick={startNew}
              className="px-3 py-2 text-xs bg-primary text-white rounded-lg hover:opacity-90 flex items-center gap-1.5"
              data-testid="add-provider-btn">
              <Plus className="w-3.5 h-3.5" /> Add provider
            </button>
          )}
        </div>
      </div>

      {showImport && <ImportPanel token={token} onDone={load} />}
      {editing && <EditForm initial={editing} token={token} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}

      {/* Filters */}
      <div className="bg-white border border-border rounded-xl p-3 space-y-2" data-testid="provider-filters">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Filter className="w-3 h-3" /> Filters
          <button onClick={() => setFilters({ status: '', category: '', province: '', postcode: '', affiliate_open: '', min_score: '', min_rating: '', min_reviews: '', search: '', sort: 'score_desc' })}
            className="ml-auto text-[10px] text-primary hover:underline">Reset</button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
          <select value={filters.status} onChange={e => updateFilter('status', e.target.value)}
            className="text-xs border border-border rounded px-2 py-1.5 bg-white" data-testid="filter-status">
            <option value="">All statuses</option>
            {Object.keys(STATUS_META).map(s => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
          </select>
          <select value={filters.category} onChange={e => updateFilter('category', e.target.value)}
            className="text-xs border border-border rounded px-2 py-1.5 bg-white" data-testid="filter-category">
            <option value="">All categories</option>
            {CATEGORY_OPTIONS.map(c => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
          </select>
          <select value={filters.province} onChange={e => updateFilter('province', e.target.value)}
            className="text-xs border border-border rounded px-2 py-1.5 bg-white" data-testid="filter-province">
            <option value="">All provinces</option>
            {PROVINCES.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <input value={filters.postcode} onChange={e => updateFilter('postcode', e.target.value)}
            placeholder="Postcode (e.g. 1012)"
            className="text-xs border border-border rounded px-2 py-1.5" data-testid="filter-postcode" />
          <select value={filters.affiliate_open} onChange={e => updateFilter('affiliate_open', e.target.value)}
            className="text-xs border border-border rounded px-2 py-1.5 bg-white" data-testid="filter-affiliate-open">
            <option value="">All affiliate</option>
            {AFFILIATE_OPTIONS.map(s => <option key={s} value={s}>{AFFILIATE_META[s].label}</option>)}
          </select>
          <input type="number" min="0" max="100" value={filters.min_score} onChange={e => updateFilter('min_score', e.target.value)}
            placeholder="Min score"
            className="text-xs border border-border rounded px-2 py-1.5" data-testid="filter-min-score" />
          <input type="number" min="0" max="5" step="0.1" value={filters.min_rating} onChange={e => updateFilter('min_rating', e.target.value)}
            placeholder="Min rating"
            className="text-xs border border-border rounded px-2 py-1.5" data-testid="filter-min-rating" />
          <input type="number" min="0" value={filters.min_reviews} onChange={e => updateFilter('min_reviews', e.target.value)}
            placeholder="Min reviews"
            className="text-xs border border-border rounded px-2 py-1.5" data-testid="filter-min-reviews" />
          <input value={filters.search} onChange={e => updateFilter('search', e.target.value)}
            placeholder="Search company/email"
            className="text-xs border border-border rounded px-2 py-1.5" data-testid="filter-search" />
          <select value={filters.sort} onChange={e => updateFilter('sort', e.target.value)}
            className="text-xs border border-border rounded px-2 py-1.5 bg-white" data-testid="filter-sort">
            <option value="score_desc">Score ↓</option>
            <option value="rating_desc">Rating ↓</option>
            <option value="reviews_desc">Reviews ↓</option>
            <option value="created_desc">Newest</option>
            <option value="priority_asc">Priority ↑</option>
          </select>
        </div>

        {/* Status counts strip */}
        <div className="flex flex-wrap gap-2 pt-1 text-[11px]">
          {Object.entries(STATUS_META).map(([s, meta]) => (
            <button key={s} onClick={() => updateFilter('status', filters.status === s ? '' : s)}
              className={`px-2 py-0.5 rounded-full ${filters.status === s ? 'ring-2 ring-primary ring-offset-1' : ''} ${meta.cls}`}
              data-testid={`status-count-${s}`}>
              {meta.label}: <strong>{counts[s] || 0}</strong>
            </button>
          ))}
        </div>
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="bg-primary/10 border border-primary rounded-xl p-3 flex flex-wrap items-center gap-2 sticky top-2 z-10" data-testid="bulk-bar">
          <span className="text-xs font-medium">{selected.size} selected</span>
          <span className="text-[10px] text-muted-foreground">(max 50 per bulk action)</span>
          <div className="flex-1" />
          <button onClick={() => bulkSetStatus('active')}
            disabled={selected.size > 50}
            className="px-3 py-1 text-[11px] bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 flex items-center gap-1"
            data-testid="bulk-approve-btn">
            <CheckCircle2 className="w-3 h-3" /> Approve
          </button>
          <button onClick={() => bulkSetStatus('paused')}
            className="px-3 py-1 text-[11px] bg-slate-600 text-white rounded hover:bg-slate-700 flex items-center gap-1">
            <Pause className="w-3 h-3" /> Pause
          </button>
          <button onClick={() => bulkSetStatus('needs_check')}
            className="px-3 py-1 text-[11px] bg-amber-600 text-white rounded hover:bg-amber-700 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> Needs check
          </button>
          <button onClick={() => bulkSetStatus('rejected')}
            className="px-3 py-1 text-[11px] bg-red-600 text-white rounded hover:bg-red-700 flex items-center gap-1"
            data-testid="bulk-reject-btn">
            <ShieldX className="w-3 h-3" /> Reject
          </button>
          <button onClick={clearSelection} className="px-2 py-1 text-[11px] text-muted-foreground hover:text-foreground">
            Clear
          </button>
        </div>
      )}

      {/* List */}
      {loading ? (
        <p className="text-sm text-muted-foreground py-8 text-center">Loading…</p>
      ) : providers.length === 0 ? (
        <div className="bg-white rounded-xl border border-border p-8 text-center">
          <Users className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No providers match the current filters.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {providers.map(p => {
            const sel = selected.has(p.provider_id);
            return (
              <div key={p.provider_id}
                className={`bg-white border rounded-xl p-3 ${sel ? 'border-primary bg-primary/5' : 'border-border'}`}
                data-testid={`provider-row-${p.provider_id}`}>
                <div className="flex items-start gap-3">
                  <input type="checkbox" checked={sel} onChange={() => toggleSelected(p.provider_id)}
                    className="mt-1" data-testid={`select-${p.provider_id}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">{p.company}</span>
                      {p.contact_name && <span className="text-xs text-muted-foreground">· {p.contact_name}</span>}
                      <StatusPill status={p.status || 'imported'} />
                      <ScoreBadge score={p.score} />
                      {p.google_rating > 0 && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-yellow-100 text-yellow-800">
                          ★ {p.google_rating} · {p.review_count}
                        </span>
                      )}
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700">Prio {p.priority ?? 5}</span>
                      {p.affiliate_open && p.affiliate_open !== 'unknown' && (
                        <span
                          className={`text-[10px] px-1.5 py-0.5 rounded-full ${(AFFILIATE_META[p.affiliate_open] || AFFILIATE_META.unknown).cls}`}
                          data-testid={`affiliate-chip-${p.provider_id}`}
                          title={`Affiliate: ${(AFFILIATE_META[p.affiliate_open] || AFFILIATE_META.unknown).label}`}
                        >
                          Aff: {(AFFILIATE_META[p.affiliate_open] || AFFILIATE_META.unknown).label}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground mt-1">
                      <span>{p.email}</span>
                      {p.phone && <span>{p.phone}</span>}
                      {p.website && <a href={p.website} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline truncate max-w-[200px]">{p.website}</a>}
                    </div>
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {(p.categories || []).map(c => (
                        <span key={c} className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary">{c.replace(/_/g, ' ')}</span>
                      ))}
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-1 space-x-2">
                      {(p.provinces && p.provinces.length > 0)
                        ? <span>{p.provinces.join(' · ')}</span>
                        : (p.province && <span>{p.province}</span>)}
                      {(p.postcode_ranges || []).length > 0 && <span>· Ranges: {(p.postcode_ranges || []).map(r => `${r.from}-${r.to}`).join(', ')}</span>}
                      {(p.service_cities || []).length > 0 && <span>· Cities: {(p.service_cities || []).join(', ')}</span>}
                      {p.nationwide && <span className="text-emerald-700">· Nationwide</span>}
                      {p.response_time_tested && <span>· Resp ~{p.response_time_hours}h</span>}
                    </div>
                    {(p.risk_flags || []).length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {(p.risk_flags || []).map(f => (
                          <span key={f} className="text-[10px] px-1.5 py-0.5 rounded bg-red-50 text-red-700">⚠ {f.replace(/_/g, ' ')}</span>
                        ))}
                      </div>
                    )}
                    {p.reject_reason && (
                      <p className="text-[11px] text-red-700 mt-1">Rejected: {p.reject_reason}</p>
                    )}
                  </div>

                  <div className="flex flex-shrink-0 flex-col gap-1 items-end">
                    <button onClick={() => startEdit(p)} title="Edit"
                      className="p-1.5 text-muted-foreground hover:text-primary"
                      data-testid={`edit-provider-${p.provider_id}`}>
                      <Edit3 className="w-3.5 h-3.5" />
                    </button>
                    <div className="flex gap-1">
                      {p.status !== 'active' && (
                        <button onClick={() => setStatus(p.provider_id, 'active')}
                          className="px-2 py-1 text-[10px] bg-green-600 text-white rounded hover:bg-green-700 flex items-center gap-0.5"
                          data-testid={`approve-${p.provider_id}`} title="Approve (set active)">
                          <CheckCircle2 className="w-3 h-3" /> Approve
                        </button>
                      )}
                      {p.status === 'active' && (
                        <button onClick={() => setStatus(p.provider_id, 'paused')}
                          className="px-2 py-1 text-[10px] bg-slate-600 text-white rounded hover:bg-slate-700 flex items-center gap-0.5"
                          data-testid={`pause-${p.provider_id}`} title="Pause">
                          <Pause className="w-3 h-3" /> Pause
                        </button>
                      )}
                      {p.status === 'paused' && (
                        <button onClick={() => setStatus(p.provider_id, 'active')}
                          className="px-2 py-1 text-[10px] bg-green-600 text-white rounded hover:bg-green-700 flex items-center gap-0.5"
                          data-testid={`resume-${p.provider_id}`} title="Resume">
                          <Play className="w-3 h-3" /> Resume
                        </button>
                      )}
                      {p.status !== 'rejected' && (
                        <button onClick={() => reject(p.provider_id)}
                          className="px-2 py-1 text-[10px] bg-white border border-red-300 text-red-700 rounded hover:bg-red-50 flex items-center gap-0.5"
                          data-testid={`reject-${p.provider_id}`} title="Reject">
                          <ShieldX className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
